import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import logoDark from "../assets/logo-dark.svg";
import { useAuth } from "../lib/auth.jsx";
import { useScope } from "../lib/scope.jsx";
import { useTheme } from "../lib/hooks.js";
import ChangePassword from "./ChangePassword.jsx";
import ChangeEmail from "./ChangeEmail.jsx";
import { Empty, Loading } from "./ui.jsx";
import {
  IconAlert,
  IconChart,
  IconCommand,
  IconDashboard,
  IconDevices,
  IconKey,
  IconLayers,
  IconLogout,
  IconMenu,
  IconMoon,
  IconSun,
  IconTenants,
  IconToken,
  IconUsers,
} from "./icons.jsx";

/** Navigation grouped by the job being done rather than by API shape. */
const NAV = [
  {
    label: "Operate",
    items: [
      { to: "/", end: true, label: "Dashboard", icon: IconDashboard },
      { to: "/devices", label: "Devices", icon: IconDevices },
      { to: "/commands", label: "Commands", icon: IconCommand },
      { to: "/vending", label: "Vending", icon: IconToken },
    ],
  },
  {
    label: "Analyse",
    items: [
      { to: "/usage", label: "Usage", icon: IconChart },
      { to: "/alerts", label: "Alerts", icon: IconAlert },
    ],
  },
  {
    label: "Administer",
    items: [
      { to: "/tenants", label: "Tenants", icon: IconTenants, adminOnly: true },
      { to: "/models", label: "Hardware models", icon: IconLayers },
      { to: "/production", label: "Production runs", icon: IconDevices },
      { to: "/saas", label: "Device batch SaaS", icon: IconLayers, adminOnly: true },
      { to: "/operators", label: "Operators", icon: IconUsers, adminOnly: true },
    ],
  },
];

const TITLES = {
  "/": ["Dashboard", "Estate overview"],
  "/devices": ["Devices", "Registry"],
  "/commands": ["Commands", "Device control"],
  "/vending": ["Vending", "Token issuance"],
  "/usage": ["Usage", "Consumption analytics"],
  "/alerts": ["Alerts", "Device events"],
  "/tenants": ["Tenants", "Platform administration"],
  "/models": ["Hardware models", "Platform administration"],
  "/production": ["Production runs", "Platform administration"],
  "/saas": ["Device batch SaaS", "Commercial agreements"],
  "/operators": ["Operators", "Platform administration"],
};

// Screens that read or change one tenant's estate. They cannot render until a
// tenant has been chosen, so the layout shows the picker instead of letting
// each page fail on its own with an error that does not say what to do.
const SCOPED_ROUTES = new Set(["/", "/devices", "/commands", "/vending", "/usage", "/alerts"]);

export default function Layout() {
  const { session, signOut, isAdmin } = useAuth();
  const scope = useScope();
  const [theme, toggleTheme] = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changingEmail,setChangingEmail]=useState(false);
  const location = useLocation();

  const root = "/" + (location.pathname.split("/")[1] ?? "");
  const [title, breadcrumb] = TITLES[root === "/" ? "/" : root] ?? ["", ""];
  const initials = (session?.email ?? "?").slice(0, 2).toUpperCase();
  const needsScope = SCOPED_ROUTES.has(root) && !scope.alias;

  return (
    <div className="app-shell">
      <aside className={`sidebar${menuOpen ? " open" : ""}`}>
        <div className="sidebar-brand">
          <img src={logoDark} alt="MeshNova" />
        </div>

        <nav className="sidebar-nav">
          {NAV.map((section) => {
            const items = section.items.filter((i) => !i.adminOnly || isAdmin);
            if (items.length === 0) return null;
            return (
              <div className="nav-section" key={section.label}>
                <div className="nav-section-label">{section.label}</div>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <item.icon />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-chip">
            <div className="avatar">{initials}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="user-chip-name" title={session?.email}>
                {session?.email}
              </div>
              <div className="user-chip-role">{roleLabel(session?.role)}</div>
            </div>
            <button className="btn btn-ghost" onClick={()=>setChangingEmail(true)} title="Change login email">Email</button>
              <button
              className="btn btn-ghost btn-sm"
              style={{ color: "rgba(255,255,255,0.6)" }}
              onClick={() => setChangingPassword(true)}
              title="Change your password"
              aria-label="Change your password"
            >
              <IconKey />
            </button>
          </div>
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              className="btn btn-ghost btn-sm menu-toggle"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle navigation"
            >
              <IconMenu />
            </button>
            <div className="topbar-title">
              <h1>{title}</h1>
              {breadcrumb && <span className="breadcrumb">{breadcrumb}</span>}
            </div>
          </div>

          <div className="topbar-actions">
            <TenantPicker scope={scope} />
            <button
              className="btn btn-ghost btn-sm"
              onClick={toggleTheme}
              aria-label={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
              title={theme === "light" ? "Dark theme" : "Light theme"}
            >
              {theme === "light" ? <IconMoon /> : <IconSun />}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => signOut()}>
              <IconLogout />
              Sign out
            </button>
          </div>
        </header>

        <main className="page">
          {needsScope ? <ChooseTenant scope={scope} /> : <Outlet />}
        </main>
      </div>

      {changingEmail&&<ChangeEmail onClose={()=>setChangingEmail(false)}/>}
      {changingPassword && <ChangePassword onClose={() => setChangingPassword(false)} />}
    </div>
  );
}

/**
 * TenantPicker names the estate every operational request is sent against.
 *
 * It sits in the top bar on every screen, including the administration ones
 * where it has no effect, so its value is never out of sight when somebody is
 * about to act.
 */
function TenantPicker({ scope }) {
  if (scope.loading) return <span className="hint">Loading tenants…</span>;
  if (scope.tenants.length === 0) return null;

  return (
    <div className="tenant-picker">
      <IconTenants />
      <label className="sr-only" htmlFor="tenant-scope">
        Acting within tenant
      </label>
      <select
        id="tenant-scope"
        className="input input-sm"
        value={scope.alias}
        onChange={(e) => scope.setAlias(e.target.value)}
      >
        <option value="">Choose a tenant…</option>
        {scope.tenants.map((t) => (
          <option key={t.Alias} value={t.Alias}>
            {t.Name} ({t.Alias}){t.Active ? "" : " (inactive)"}
          </option>
        ))}
      </select>
    </div>
  );
}

/** ChooseTenant explains why a screen is empty and what to do about it. */
function ChooseTenant({ scope }) {
  if (scope.loading) return <Loading label="Loading tenants" />;

  if (scope.tenants.length === 0) {
    return (
      <Empty title="No tenants yet">
        Devices, commands and vending all belong to a tenant. Create the first one
        under Tenants, then come back here.
      </Empty>
    );
  }

  return (
    <Empty title="Choose a tenant">
      You can see every tenant on the platform, so this screen needs to know which
      estate you mean. Pick one from the selector in the top bar.
    </Empty>
  );
}

function roleLabel(role) {
  switch (role) {
    case "SYSTEM_ADMIN":
      return "System administrator";
    case "OPERATOR":
      return "Operator";
    case "READ_ONLY":
      return "Read only";
    default:
      return "Signed in";
  }
}
